import React from "react";
var __jsx = React.createElement;
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import WarrantyOptionItem from "@/pages/checkout/warranty/WarrantyOptionItem";
import { useTranslation } from "@i18n";
import WarrantyCollapsedHeader from "./WarrantyCollapsedHeader";
import { Icon } from "@syarahtech/ui";
import { twMerge } from "tailwind-merge";
function ChevronIcon(_ref) {
  var up = _ref.up;
  return /*#__PURE__*/React.createElement(Icon, {
    icon: up ? "material-symbols--keyboard-arrow-up" : "material-symbols--keyboard-arrow-down",
    className: "text-primary-new font-bold",
    width: 5,
    height: 5
  });
}
export default function ExpandableWarrantySelector(_ref2) {
  var _warrantyData$items5;
  var warranty_promo_code = _ref2.warranty_promo_code,
    warrantyData = _ref2.warrantyData,
    warrantyPriceDetails = _ref2.warrantyPriceDetails,
    onListOpened = _ref2.onListOpened,
    onWarrantySelected = _ref2.onWarrantySelected;
  var _useTranslation = useTranslation(["web_checkout_cash"]),
    tShort = _useTranslation.tShort;

  // All hooks must run unconditionally (Rules of Hooks)
  var _useState = useState(false),
    isExpanded = _useState[0],
    setIsExpanded = _useState[1];

  // Only pre-select if an item is explicitly selected in the API response.
  // When warrantyPriceDetailsV3 is null and no item is selected, leave undefined
  // so nothing appears as "selected" in the list.
  var _useState2 = useState(function () {
      var _warrantyData$items, _warrantyData$items$f, _warrantyData$items2, _warrantyData$items2$;
      var preSelected = warrantyData === null || warrantyData === void 0 ? void 0 : (_warrantyData$items = warrantyData.items) === null || _warrantyData$items === void 0 ? void 0 : (_warrantyData$items$f = _warrantyData$items.find(function (i) {
        return i.selected;
      })) === null || _warrantyData$items$f === void 0 ? void 0 : _warrantyData$items$f.id;
      if (preSelected) return preSelected;
      // Only default to first item if price details are available (existing warranty case)
      var hasPD = !!warrantyPriceDetails && !Array.isArray(warrantyPriceDetails);
      if (hasPD) return warrantyData === null || warrantyData === void 0 ? void 0 : (_warrantyData$items2 = warrantyData.items) === null || _warrantyData$items2 === void 0 ? void 0 : (_warrantyData$items2$ = _warrantyData$items2[0]) === null || _warrantyData$items2$ === void 0 ? void 0 : _warrantyData$items2$.id;
      return undefined;
    }),
    selectedId = _useState2[0],
    setSelectedId = _useState2[1];

  // Re-sync with the API-reported selection whenever warrantyData changes
  // (e.g. after navigating back/forward where the API returns selected: true).
  useEffect(function () {
    var _warrantyData$items$f2, _warrantyData$items3, _warrantyData$items3$, _warrantyData$items4, _warrantyData$items4$;
    var serverSelectedId = (_warrantyData$items$f2 = warrantyData === null || warrantyData === void 0 ? void 0 : (_warrantyData$items3 = warrantyData.items) === null || _warrantyData$items3 === void 0 ? void 0 : (_warrantyData$items3$ = _warrantyData$items3.find(function (i) {
      return i.selected;
    })) === null || _warrantyData$items3$ === void 0 ? void 0 : _warrantyData$items3$.id) !== null && _warrantyData$items$f2 !== void 0 ? _warrantyData$items$f2 : warrantyData === null || warrantyData === void 0 ? void 0 : (_warrantyData$items4 = warrantyData.items) === null || _warrantyData$items4 === void 0 ? void 0 : (_warrantyData$items4$ = _warrantyData$items4[0]) === null || _warrantyData$items4$ === void 0 ? void 0 : _warrantyData$items4$.id;
    if (serverSelectedId != null && serverSelectedId !== selectedId) {
      setSelectedId(serverSelectedId);
    }
  }, [warrantyData]);

  // warrantyPriceDetailsV3 can arrive as [] (empty array) or a real object.
  // Use Array.isArray to distinguish — an array means "not available".
  var hasPriceDetails = !!warrantyPriceDetails && !Array.isArray(warrantyPriceDetails);
  var hasItems = (warrantyData === null || warrantyData === void 0 ? void 0 : (_warrantyData$items5 = warrantyData.items) === null || _warrantyData$items5 === void 0 ? void 0 : _warrantyData$items5.length) > 1;

  // Nothing to show at all
  if (!hasPriceDetails && !hasItems) return null;
  var selectedIndex = hasItems ? warrantyData.items.findIndex(function (i) {
    return i.id === selectedId;
  }) : -1;
  return /*#__PURE__*/React.createElement(React.Fragment, null, hasPriceDetails && (warrantyPriceDetails === null || warrantyPriceDetails === void 0 ? void 0 : warrantyPriceDetails.title) && !hasItems && /*#__PURE__*/React.createElement("div", {
    className: twMerge(warranty_promo_code && "rounded-lg border border-solid border-[#d6dfe9] bg-[#f1f6fc] p-2 w-full")
  }, /*#__PURE__*/React.createElement(WarrantyCollapsedHeader, {
    warranty_promo_code_enabled: warranty_promo_code,
    warrantyPriceDetails: warrantyPriceDetails
  })), hasItems && /*#__PURE__*/React.createElement("div", {
    className: "rounded-lg border border-solid border-[#d6dfe9] bg-[#f1f6fc] p-3 w-full"
  }, hasPriceDetails && (warrantyPriceDetails === null || warrantyPriceDetails === void 0 ? void 0 : warrantyPriceDetails.title) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(WarrantyCollapsedHeader, {
    warrantyPriceDetails: warrantyPriceDetails
  }), /*#__PURE__*/React.createElement("div", {
    className: "w-full h-px bg-[#d6dfe9] my-3"
  })), warrantyData.items.length > 1 && /*#__PURE__*/React.createElement("div", {
    onClick: function onClick() {
      var next = !isExpanded;
      setIsExpanded(next);
      if (next) onListOpened === null || onListOpened === void 0 ? void 0 : onListOpened();
    },
    className: "cursor-pointer flex items-center gap-2 mx-auto justify-center hover:opacity-80 transition-opacity"
  }, selectedIndex < warrantyData.items.length - 1 && /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--add-moderator-outline-rounded",
    className: "text-[#8E47E0]",
    width: 6,
    height: 6
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-primary-new font-bold text-[14px] leading-tight"
  }, tShort(selectedIndex === warrantyData.items.length - 1 ? "warranty/change_title" : "warranty/extend_title", {
    ns: "web_checkout_cash"
  })), selectedIndex === warrantyData.items.length - 1 ? /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--edit-square-outline",
    className: "text-primary-new px-1",
    width: 5,
    height: 5
  }) : /*#__PURE__*/React.createElement(ChevronIcon, {
    up: isExpanded
  })), /*#__PURE__*/React.createElement(AnimatePresence, {
    initial: false
  }, isExpanded && /*#__PURE__*/React.createElement(motion.div, {
    key: "warranty-list",
    initial: {
      height: 0,
      opacity: 0
    },
    animate: {
      height: "auto",
      opacity: 1
    },
    exit: {
      height: 0,
      opacity: 0
    },
    transition: {
      duration: 0.28,
      ease: [0.4, 0, 0.2, 1]
    },
    style: {
      overflow: "hidden",
      padding: "2px"
    }
  }, /*#__PURE__*/React.createElement(motion.div, {
    className: "flex flex-col gap-2 pt-3",
    initial: "hidden",
    animate: "visible",
    variants: {
      visible: {
        transition: {
          staggerChildren: 0.06,
          delayChildren: 0.05
        }
      },
      hidden: {}
    }
  }, warrantyData.items.map(function (item, itemIndex) {
    return /*#__PURE__*/React.createElement(motion.div, {
      key: item.id,
      variants: {
        hidden: {
          opacity: 0,
          y: 10
        },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.25,
            ease: "easeOut"
          }
        }
      },
      className: "mt-2"
    }, /*#__PURE__*/React.createElement(WarrantyOptionItem, {
      item: item,
      isSelected: item.id === selectedId,
      onSelect: function onSelect(id) {
        setSelectedId(id);
        setIsExpanded(false);
        onWarrantySelected === null || onWarrantySelected === void 0 ? void 0 : onWarrantySelected(id);
      },
      size: "md",
      showBadge: true,
      animateGlow: Boolean(item.badge) && selectedIndex < itemIndex
    }));
  }))))));
}